import { useCurrentClub } from '@/hooks/use-current-club';
import { useAsync } from '@repo/ui/hooks/use-async';
import { useForm } from '@repo/ui/hooks/use-form';
import { clubApi } from '@repo/api-client';
import { Input } from '@repo/ui/components/form/input';
import { Button } from '@repo/ui/components/button';
import { Select } from '@repo/ui/components/form/select';
import {
  CreateOpenMatch,
  UpdateOpenMatch,
  WebOpenMatch,
  Category,
  Court,
} from '@repo/shared-types';
import { useRevalidator } from 'react-router';
import { GeneralError } from '@repo/ui/components/form/general-error';

interface CreateOpenMatchFormProps {
  courts: Court[];
  categories: Category[];
  onClose: () => void;
  initialData?: WebOpenMatch;
}

export function CreateOpenMatchForm({
  courts,
  categories,
  onClose,
  initialData,
}: CreateOpenMatchFormProps) {
  const club = useCurrentClub();
  const revalidator = useRevalidator();
  const isEditMode = !!initialData;

  // Parse initial data for form
  const getInitialValues = () => {
    if (initialData) {
      const startDate = new Date(initialData.dateTime);
      const dateStr = startDate.toISOString().split('T')[0];
      const timeStr = startDate.toTimeString().slice(0, 5); // HH:mm format

      return {
        courtId: initialData.court.id,
        date: dateStr,
        startTime: timeStr,
        duration: initialData.duration.toString(),
        categoryId: initialData.category?.id || '',
        playerOneName: initialData.playerOneName || '',
        playerTwoName: initialData.playerTwoName || '',
        playerThreeName: initialData.playerThreeName || '',
        playerFourName: initialData.playerFourName || '',
      };
    }

    // Default values for new match
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dateStr = tomorrow.toISOString().split('T')[0];

    return {
      courtId: '',
      date: dateStr,
      startTime: '18:00',
      duration: '90',
      categoryId: '',
      playerOneName: '',
      playerTwoName: '',
      playerThreeName: '',
      playerFourName: '',
    };
  };

  const {
    execute: onSubmit,
    error: apiErrors,
    loading,
  } = useAsync(async () => {
    const date = new Date(`${values.date}T${values.startTime}:00`);
    const durationMinutes = parseInt(values.duration, 10);
    const endDate = new Date(date.getTime() + durationMinutes * 60 * 1000);

    const data: CreateOpenMatch | UpdateOpenMatch = {
      courtId: values.courtId,
      start: date.toISOString(),
      end: endDate.toISOString(),
      categoryId: values.categoryId || undefined,
      playerOneName: values.playerOneName || undefined,
      playerTwoName: values.playerTwoName || undefined,
      playerThreeName: values.playerThreeName || undefined,
      playerFourName: values.playerFourName || undefined,
    };

    if (isEditMode && initialData) {
      await clubApi.updateOpenMatch(club.id, initialData.id, data);
    } else {
      await clubApi.createOpenMatch(club.id, data as CreateOpenMatch);
    }

    onClose();
    revalidator.revalidate();
  });

  const {
    ref,
    values,
    errors: formErrors,
  } = useForm({
    initialValues: getInitialValues(),
    onSubmit,
  });

  const errors = { ...formErrors, ...apiErrors };

  return (
    <form ref={ref} className="w-full flex flex-col gap-4">
      <Select
        label="Cancha"
        name="courtId"
        required
        errors={errors}
        options={[
          { label: 'Selecciona una cancha', value: '' },
          ...courts.map((court) => ({
            label: court.name,
            value: court.id,
          })),
        ]}
      />

      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Fecha"
          type="date"
          name="date"
          required
          errors={errors}
          min={new Date().toISOString().split('T')[0]}
        />
        <Input label="Hora de inicio" type="time" name="startTime" required errors={errors} />
      </div>

      <Input
        label="Duración (minutos)"
        type="number"
        name="duration"
        required
        errors={errors}
        min={30}
        max={240}
        description="Duración del partido en minutos (30-240)"
      />

      <Select
        label="Categoría (opcional)"
        name="categoryId"
        errors={errors}
        options={[
          { label: 'Sin categoría', value: '' },
          ...(categories || [])
            .filter((cat) => cat.isUserEligible)
            .map((category) => ({
              label: category.name,
              value: category.id,
            })),
        ]}
      />

      <div className="space-y-3">
        <p className="text-sm font-medium text-muted-foreground">Jugadores (opcional)</p>
        <Input
          label="Jugador 1"
          type="text"
          name="playerOneName"
          errors={errors}
          placeholder="Nombre del jugador"
        />
        <Input
          label="Jugador 2"
          type="text"
          name="playerTwoName"
          errors={errors}
          placeholder="Nombre del jugador"
        />
        <Input
          label="Jugador 3"
          type="text"
          name="playerThreeName"
          errors={errors}
          placeholder="Nombre del jugador"
        />
        <Input
          label="Jugador 4"
          type="text"
          name="playerFourName"
          errors={errors}
          placeholder="Nombre del jugador"
        />
      </div>

      <GeneralError errors={errors} />
      <Button type="submit" disabled={loading}>
        {loading ? 'Guardando...' : isEditMode ? 'Actualizar' : 'Crear'}
      </Button>
    </form>
  );
}
